import { LoadingSpinner } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { Container, Stack, ThemeProvider } from "@mui/material";
import { useEffect } from "react";

import { PageWrapper } from "../../../redesign/components/PageWrapper";
import { APP_V2_APP_EVENTS, logEvent } from "../../analytics";
import { ClipboardLogo } from "../../Content";

interface AppLoadingPageProps {
  parentEventName?: string;
  isDebuggingEnabled?: boolean;
  debugTimeoutInMs?: number;
}

export function AppLoadingPage({
  parentEventName,
  isDebuggingEnabled = false,
  debugTimeoutInMs = 20_000,
}: AppLoadingPageProps = {}) {
  useEffect(() => {
    logEvent(APP_V2_APP_EVENTS.APP_LOADING_PAGE_EFFECT_RUNNING, {
      parentEventName,
      isDebuggingEnabled,
      debugTimeoutInMs,
    });
    if (!isDebuggingEnabled || !parentEventName) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      logEvent(APP_V2_APP_EVENTS.APP_LOADING_TIMEOUT, {
        parentEventName,
        timeoutSeconds: debugTimeoutInMs / 1000,
        timestamp: new Date().toISOString(),
      });
    }, debugTimeoutInMs); // 20 seconds

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isDebuggingEnabled, parentEventName, debugTimeoutInMs]);

  // We are not using useLogEffect because we actually want to see all the instances this is rendered.
  // This is especially true to know what is happening when app move from background to foreground
  // Remove after splash screen investigation
  logEvent(APP_V2_APP_EVENTS.APP_LOADING_PAGE_DISPLAYED, {
    parentEventName,
    isDebuggingEnabled,
    debugTimeoutInMs,
  });

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={{
          background: (theme) => theme.palette.intent?.neutral.gradient,
          ...getSafeAreaPaddings({ bottom: true, fallbackBottom: "24px" }),
          paddingX: 2,
        }}
      >
        <Container
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack
            spacing={8}
            alignItems="center"
            textAlign="center"
            sx={{
              flex: 1,
              justifyContent: "center",
              mb: 3,
            }}
          >
            <ClipboardLogo width={160} />
            <LoadingSpinner size="large" variant="primary" />
          </Stack>
        </Container>
      </PageWrapper>
    </ThemeProvider>
  );
}
